import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useSession } from "~/api/queries";
import { reasoningRequestIds } from "~/api/reasoningRequestIds";
import type { DirectReasoningRequest } from "~/api/reasoningTypes";
import { isTerminalJobStatus } from "./reasoningSelectors";
import {
  clearReasoningRecovery,
  readReasoningRecovery,
  reasoningRequestKey,
  saveReasoningRecovery,
} from "./recoveryStorage";

type Pending = {
  input: DirectReasoningRequest & { commandId: string };
  runId: string;
  sessionId: string;
};

export const REQUEST_POLL_MS = 3000;
/** After two minutes on one request the status poll slows to this. */
export const REQUEST_POLL_SLOW_MS = 15000;
export const REQUEST_POLL_BACKOFF_AFTER_MS = 2 * 60 * 1000;

/**
 * Persist before dispatch. Reload and transport failures only offer an explicit replay of the
 * same command. The status poll stops once the job is terminal and, for a completed job, once an
 * evaluation is known to exist — either because the caller saw one (`evaluated`) or because one
 * trace read after completion found it. A finished request costs nothing on later visits.
 */
export function useReasoningRequest(options: { evaluated?: boolean } = {}) {
  const { t } = useTranslation();
  const api = useApi();
  const { data: identity } = useSession();
  const key = identity ? reasoningRequestKey(identity.workspaceId, identity.userId) : null;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [traceEvaluated, setTraceEvaluated] = useState(false);
  const flight = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    flight.current = false;
    setBusy(false);
    setPending(null);
    setError("");
    setStatus("");
    setJobStatus("");
    setTraceEvaluated(false);
    setLoadedKey(key);
    if (!key) return;
    try {
      const stored = readReasoningRecovery(key);
      if (stored) {
        const parsed = JSON.parse(stored) as Pending;
        if (!parsed.input?.commandId || !parsed.runId || !parsed.sessionId)
          throw new Error("Invalid saved request");
        setPending(parsed);
        setStatus(t("Recovered request. Inspect its run or retry the same request."));
      }
    } catch {
      setError(
        t("The saved request could not be loaded. Check recent runs before starting again."),
      );
    }
    return () => {
      generation.current++;
    };
  }, [key, t]);

  const evaluated = options.evaluated === true || traceEvaluated;
  const settled =
    !!jobStatus &&
    isTerminalJobStatus(jobStatus) &&
    (jobStatus.toLowerCase() !== "completed" || evaluated);
  useEffect(() => {
    if (!pending || loadedKey !== key || settled) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();
    const poll = async () => {
      let latest = "";
      try {
        const job = await api.reasoning.requestStatus(pending.runId);
        if (active) {
          latest = job.status;
          setJobStatus(job.status);
          setStatus(`${job.status}${job.error ? `: ${job.error}` : ""}`);
        }
      } catch {
        /* Context assembly may still be committing. */
      }
      if (active && latest.toLowerCase() === "completed" && !evaluated) {
        // One read, not a loop: a completed job's evaluation is either recorded or never will be.
        try {
          const result = await api.reasoning.trace(pending.sessionId);
          const operation = result.operations.at(-1);
          if (active && operation?.evaluationRecordId) setTraceEvaluated(true);
          if (active && operation && operation.status !== "running")
            setStatus(`${operation.status}${operation.error ? `: ${operation.error}` : ""}`);
        } catch {
          /* A retained session may not exist until context assembly completes. */
        }
      }
      if (active)
        timer = setTimeout(
          poll,
          Date.now() - startedAt > REQUEST_POLL_BACKOFF_AFTER_MS
            ? REQUEST_POLL_SLOW_MS
            : REQUEST_POLL_MS,
        );
    };
    void poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [api, pending, loadedKey, key, settled, evaluated]);

  async function submit(input?: DirectReasoningRequest) {
    if (flight.current || !key || loadedKey !== key) return;
    flight.current = true;
    setBusy(true);
    setError("");
    const epoch = generation.current;
    try {
      let request = pending;
      if (!request) {
        if (!input) return;
        const commandId = input.commandId ?? crypto.randomUUID();
        request = {
          input: { ...input, commandId, background: true },
          ...(await reasoningRequestIds(commandId)),
        };
        if (epoch !== generation.current) return;
        saveReasoningRecovery(key, JSON.stringify(request));
        setPending(request);
      }
      setStatus(t("Assembling retained context, generating and evaluating locally…"));
      const result = await api.reasoning.request(request.input);
      if (epoch !== generation.current) return;
      setStatus(
        "operation" in result
          ? `${result.operation.status}${result.operation.error ? `: ${result.operation.error}` : ""}`
          : `${result.status}${result.error ? `: ${result.error}` : ""}`,
      );
      if (!("operation" in result)) setJobStatus(result.status);
      return result;
    } catch (e) {
      if (epoch === generation.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (epoch === generation.current) {
        flight.current = false;
        setBusy(false);
      }
    }
  }
  function clear() {
    if (busy || !key || loadedKey !== key) return;
    clearReasoningRecovery(key);
    setPending(null);
    setStatus("");
    setJobStatus("");
    setTraceEvaluated(false);
    setError("");
  }
  async function cancel() {
    if (!pending || busy) return;
    try {
      const result = await api.reasoning.cancelRequest(pending.runId);
      setJobStatus(result.status);
      setStatus(
        result.cancelRequested
          ? t("Cancellation requested; provider outcome may be uncertain.")
          : result.status,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  async function reconcile() {
    if (!pending || busy || jobStatus !== "uncertain") return;
    try {
      const result = await api.reasoning.reconcileRequest(pending.runId);
      setJobStatus(result.operation.status);
      setStatus(
        t(
          "{{status}}: interrupted call acknowledged. Inspect the retained run before starting another operation.",
          { status: result.operation.status },
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  return {
    ready: !!key && loadedKey === key,
    pending,
    busy,
    error,
    status,
    jobStatus,
    /** The status poll has stopped: the job is final and its evaluation is known. */
    settled,
    submit,
    clear,
    cancel,
    reconcile,
  };
}
